import { Injectable } from '@angular/core';
import { BaseFormService } from '../../utils/base-form-service';
import QuillType from 'quill';
import Quill from 'devextreme-quill';
import {
  ButtonActionType,
  ButtonNavigateTo,
  HrefTarget,
} from '../administration/template-builder/template-editor-page/models/template-builder-button-widget.model';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  applyLinkStylingToElement,
  buildLinkStyleAttribute,
  LINK_STYLE_FROM_GLOBAL,
  LinkStylingFormValues,
  LinkUnderlineValue,
  parseLinkStylingFromElement,
} from './html-editor-link-styling.utils';

export class HtmlEditorLink implements LinkStylingFormValues {
  value: string = '';
  target: HrefTarget = HrefTarget.blank;
  type: ButtonActionType = ButtonActionType.link;
  subject?: string = '';
  body?: string = '';
  navigateTo?: ButtonNavigateTo = ButtonNavigateTo.web;
  portalPage?: string = '';
  linkColor: string = LINK_STYLE_FROM_GLOBAL;
  linkUnderline: LinkUnderlineValue = LINK_STYLE_FROM_GLOBAL;
  mouseoverColor: string = LINK_STYLE_FROM_GLOBAL;
  mouseoverUnderline: LinkUnderlineValue = LINK_STYLE_FROM_GLOBAL;
}

@UntilDestroy()
@Injectable()
export class HtmlEditorLinkService extends BaseFormService<HtmlEditorLink> {
  readonly linkFormValidationGroup = 'linkFormValidationGroup';
  private _selectionRange;

  constructor() {
    super();
  }

  manageLink = (quill: QuillType, activeElement: Element) => {
    this.formChangesDetector.clear();
    this._selectionRange = quill.getSelection(false);

    if (!activeElement) {
      this.getFromData();

      return;
    }

    const data = this.parseHref(activeElement);
    this.getFromData(data);
  };

  private getFromData = (initialData?: Partial<HtmlEditorLink>) => {
    const payload = Object.assign({}, new HtmlEditorLink(), initialData);
    const formData = new Proxy(payload, {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop];

        if (value !== prevValue) {
          this.formChangesDetector.handleChange(prop, value, prevValue);
          Reflect.set(target, prop, value, receiver);

          switch (prop) {
            case 'type':
              Object.assign(formData, {
                value: null,
                subject: null,
                body: null,
              });

              break;
          }

          this.formDataSig.update(data => data);
        }
        return true;
      },
    });

    this.formDataSig.set(formData);
  };

  addLink = (quill: QuillType, activeElement: Element | null): Element | null => {
    const formData = this.formDataSig();

    if (activeElement) {
      this.updateLink(quill, activeElement, formData);
      return activeElement;
    }

    const range = this._selectionRange;
    const href = this.getHref();
    const linkValue = this.buildLinkFormatValue(formData, href);

    quill.formatText(range?.index ?? 0, range?.length ?? 0, 'link', linkValue, 'user');

    const linkElement = this.findLinkElementAtRange(quill, range);

    if (!linkElement) {
      return null;
    }

    applyLinkStylingToElement(linkElement, formData);
    this.syncLinkFormatValue(quill, linkElement, formData, href);

    return linkElement;
  };

  private buildLinkFormatValue = (formData: HtmlEditorLink, href: string, linkElement?: Element) => {
    const style = linkElement?.getAttribute('style') ?? buildLinkStyleAttribute(formData);

    return {
      href,
      type: formData?.type,
      target: formData?.target,
      navigate: formData?.navigateTo,
      id: linkElement?.getAttribute('id') ?? undefined,
      style: style ?? '',
    };
  };

  private syncLinkFormatValue = (
    quill: QuillType,
    linkElement: Element,
    formData: HtmlEditorLink,
    href: string,
  ): void => {
    applyLinkStylingToElement(linkElement, formData);

    const linkBlot = Quill.find(linkElement);

    if (!linkBlot) {
      return;
    }

    const index = quill.getIndex(linkBlot);
    const length = linkBlot.length();

    quill.formatText(index, length, 'link', this.buildLinkFormatValue(formData, href, linkElement), 'user');
  };

  private updateLink = (quill: QuillType, activeElement: Element, formData: HtmlEditorLink) => {
    const href = this.getHref();

    activeElement.setAttribute('href', href);
    activeElement.setAttribute('target', formData?.target);
    activeElement.setAttribute('data-type', formData?.type);
    activeElement.setAttribute('data-navigate', formData?.navigateTo);
    this.syncLinkFormatValue(quill, activeElement, formData, href);
  };

  private findLinkElementAtRange = (quill: QuillType, range): Element | null => {
    if (!range) {
      return null;
    }

    const [leaf] = quill.getLeaf(range.index);
    let node: Node | null = leaf?.domNode ?? null;

    while (node && node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName !== 'A') {
      node = node.parentElement;
    }

    if (!node || node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    return node as Element;
  };

  private getHref = () => {
    const formData = this.formDataSig();
    const value = formData?.value;
    const type = formData?.type;
    const navigatTo = formData?.navigateTo;

    switch (type) {
      case ButtonActionType.link:
        if (navigatTo === ButtonNavigateTo.portal) {
          return formData?.portalPage;
        }
        if (/^https?:\/\//i.test(value)) {
          return value;
        }
        return `https://${value}`;
      case ButtonActionType.mailTo:
        const params = new URLSearchParams();
        if (formData?.subject) {
          params.set('subject', formData?.subject);
        }
        if (formData?.body) {
          params.set('body', formData?.body);
        }
        const mailTo = `mailto:${value}${params?.size ? '?' + params?.toString() : ''}`;

        return mailTo;
      case ButtonActionType.phone:
        return `tel:${value}`;

      default:
        return value;
    }
  };

  private parseHref = (activeElement: Element): Partial<HtmlEditorLink> => {
    const activeElementHref = activeElement.getAttribute('href');
    const activeElementTarget = activeElement.getAttribute('target') as HrefTarget;
    const activeElementType = activeElement.getAttribute('data-type');
    const activeElementNavigateTo = activeElement.getAttribute('data-navigate') as ButtonNavigateTo;

    if (
      activeElementType === ButtonActionType.mailTo ||
      activeElementHref?.toLocaleLowerCase()?.startsWith('mailto:')
    ) {
      const [url, query] = activeElementHref?.replace(/^mailto:/i, '')?.split('?');
      const params = new URLSearchParams(query);

      return {
        value: url,
        type: ButtonActionType.mailTo,
        target: activeElementTarget ?? HrefTarget.blank,
        subject: params?.get('subject'),
        body: params?.get('body'),
        ...parseLinkStylingFromElement(activeElement),
      };
    }

    if (activeElementType === ButtonActionType.phone || activeElementHref?.toLocaleLowerCase()?.startsWith('tel:')) {
      return {
        value: activeElementHref?.toLocaleLowerCase()?.replace('tel:', ''),
        type: ButtonActionType.phone,
        target: activeElementTarget ?? HrefTarget.blank,
        ...parseLinkStylingFromElement(activeElement),
      };
    }

    return {
      value: activeElementHref,
      type: ButtonActionType.link,
      target: activeElementTarget ?? HrefTarget.blank,
      navigateTo: activeElementNavigateTo ?? ButtonNavigateTo.web,
      portalPage: activeElementNavigateTo === ButtonNavigateTo.portal ? activeElementHref : '',
      ...parseLinkStylingFromElement(activeElement),
    };
  };
}
